import { useAppBuilderContext } from 'app/app/editor/context';
import { useEffect, useRef } from 'react';
import { alpha, Box } from '@mui/material';
import useBundleTranslation from 'i18n';
import IconMi from 'components/common/icon/IconMi';

export default function ComponentIcon({
    type,
    icon,
    title,
    setPreviewComponentType,
}: {
    type: string;
    icon: string;
    title: string;
    setPreviewComponentType: (type: string) => void;
}) {
    const { t } = useBundleTranslation(['app/portal-page-builder']);
    const ctx = useAppBuilderContext();
    const sendMessage = ctx.sendMessage;
    const componentItemRef = useRef<HTMLDivElement>(null);

    let preventTextSelection = (e: any) => {
        //if (e.stopPropagation) e.stopPropagation();
        if (e.preventDefault) e.preventDefault();
        //e.cancelBubble = true;
        e.returnValue = false;
        return false;
    };

    let onDragStart = (e: any) => {
        document.body.classList.add('ape-drag-active');
        if (componentItemRef.current) {
            componentItemRef.current.classList.add('active');
        }
        let draggableDiv = document.createElement('div');
        draggableDiv.innerHTML =
            '<svg width="8px" height="8px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="#000000" class="bi bi-app"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M11 2a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h6zM5 1a4 4 0 0 0-4 4v6a4 4 0 0 0 4 4h6a4 4 0 0 0 4-4V5a4 4 0 0 0-4-4H5z"></path> </g></svg>';
        draggableDiv.id = 'miDraggableDiv';
        draggableDiv.style.position = 'absolute';
        draggableDiv.style.top = e.pageY + 'px';
        draggableDiv.style.left = e.pageX + 'px';

        document.body.appendChild(draggableDiv);

        sendMessage({ name: 'dragStart', data: { type: type } });
        return preventTextSelection(e);
    };

    useEffect(() => {
        let onMouseMove: EventListener = (e: any): boolean => {
            let draggableDiv = document.getElementById('miDraggableDiv');
            if (null !== draggableDiv) {
                draggableDiv.style.top = e.pageY + 'px';
                draggableDiv.style.left = e.pageX + 'px';
            }

            return preventTextSelection(e);
        };

        let cleanDraggableDiv = () => {
            let draggableDiv = document.getElementById('miDraggableDiv');
            if (null !== draggableDiv) {
                document.body.removeChild(draggableDiv);
            }
            sendMessage({ name: 'dragEnd', data: { type: type } });
        };
        let onMouseUp: EventListener = (e: Event): void => {
            cleanDraggableDiv();
            if (componentItemRef.current) {
                componentItemRef.current.classList.remove('active');
            }
            document.body.classList.remove('ape-drag-active');
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        return () => {
            cleanDraggableDiv();
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, []);

    return (
        <Box
            sx={{
                display: 'flex',
                px: 1.5,
                py: 1,
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                borderRadius: '8px',
                '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    color: 'primary.main',
                    cursor: 'move',
                },
                '&.active': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    color: 'error.main',
                },
            }}
            onMouseEnter={() => {
                setPreviewComponentType(icon);
            }}
            onMouseLeave={() => {
                setPreviewComponentType('');
            }}
            draggable={true}
            onDragStart={onDragStart}
            ref={componentItemRef}
        >
            <Box
                sx={{
                    fontSize: '16px',
                    width: '16px',
                    height: '16px',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mr: 1,
                    mt: '1px',
                }}
            >
                <IconMi icon={icon && icon > '' ? icon : 'template'} />
            </Box>
            <Box>{title}</Box>
        </Box>
    );
}
